import { useContext } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import MetaTags from '@/components/MetaTags';

import SeoMainBanner from '@/components/SeoBlocks/SeoMainBanner';
import SearchBlock from '@/components/SeoBlocks/SearchBlock';
import DocumentsBlock from '@/components/SeoBlocks/DocumentsBlock';
import { TryConstructorBlock } from '@/components/SeoBlocks/TryConstructorBlock';
import RequestCall from '@/components/blocks/RequestCall';
import { createTreeFolders } from '@/helpers/workWithTreeFolders';
import { collectRootFolderWithAllInnerTemplates } from '@/helpers/workWithTreeFolders';
import useMetaTags from '@/hooks/useMetaTags';
import { useTheme } from '@forma/forma-ui-theme';
import getHeadersFromServer from '@/helpers/getHeadersFromServer';

import mockApi from '@/api/mockApi';
import sharedTemplatesApi from '@/api/sharedTemplatesApi';

export const revalidate = 3600;

export const getServerSideProps = async ({ req, res, params }) => {
  res.setHeader(
    'Cache-Control',
    'public, s-maxage=14400, stale-while-revalidate=10'
  );
  const headersParse = await getHeadersFromServer(req);
  try {
    const mainData = await mockApi.getMainData(params.locale.toLowerCase());
    const pageData = await mockApi.getSeoData(
      params.locale.toLowerCase(),
      'seo'
    );

    const { folders, templates } = await sharedTemplatesApi.getSharedTemplates();
    const treeWithPath = await createTreeFolders(folders, templates, true);
    const foldersRoot = collectRootFolderWithAllInnerTemplates(treeWithPath);

    return {
      props: {
        initialState: {
          main: mainData,
        },
        seoMain: pageData,
        foldersRoot,
        headersParse,
        ...(await serverSideTranslations(params.locale, ['common', 'uikit'])),
      },
    };
  } catch {
    return {
      notFound: true,
    };
  }
};

const ContentPage = ({ seoMain, foldersRoot }) => {
  const { t } = useTranslation();
  const { seo_main_banner, try_constructor_block } = seoMain;
  const { video } = try_constructor_block;
  const router = useRouter();

  const nowYear = new Date().getFullYear();
  const metaData = {
    title: t('seo_title_main', { year: nowYear }),
    description: t('seo_description_main', { year: nowYear }),
    keywords: '',
    canonicalUrl: `${process.env.SITE_URL}${router.asPath}`,
  };
  const { lang } = useTheme();
  useMetaTags({ lang, ...metaData });

  return (
    <div className="container">
      <Head>
        <title>{metaData.title}</title>
        <MetaTags {...metaData} />
      </Head>
      <SeoMainBanner {...seo_main_banner} />
      <SearchBlock />
      <DocumentsBlock documents={foldersRoot} />
      <TryConstructorBlock video={video} />
      <RequestCall
        title={t('leave_your_contacts')}
        subtitle={t('we_will_set')}
      />
    </div>
  );
};

export default ContentPage;
